'use client';

import { createHttpClient } from '@adapters/HttpClient';
import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { Input } from '@shared/components/ui/input';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import type { LoadOptionsResponse } from '@shared/components/ui/MultiSelect';
import { ProductApiAdapter } from '@shared/adapters/ProductApiAdapter';
import { ProductService } from '@shared/services/ProductService';
import { apiClientWH } from '@shared/services/ApiClient';
import { cn } from '@shared/utils/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EntryApiAdapter, EntryFilters } from '../../adapters/EntryApiAdapter';
import { Entry, ENTRY_STATUS_LABELS, EntryStatus } from '../../models/Entry';
import { ClientsApiAdapter } from '@modules/Clients/List/adapters/ClientsApiAdapter';
import { Client } from '@modules/Clients/List/models/Clients';
import { Loader2 } from 'lucide-react';

interface AdvancedEntrySelectProps {
  /** Valor seleccionado (entry_id como string) */
  value: string;
  /** Callback cuando cambia la selección */
  onValueChange: (value: string) => void;
  /** Placeholder */
  placeholder?: string;
  /** Si está deshabilitado */
  disabled?: boolean;
  /** Data test id */
  dataTestId?: string;
}

/**
 * Selector avanzado de entradas con buscador, selector de cliente y listado de cards
 * Muestra el contenido directamente inline sin modal
 */
export const AdvancedEntrySelect = ({
  value,
  onValueChange,
  placeholder = 'Seleccionar entrada...',
  disabled = false,
  dataTestId = 'advanced-entry-select',
}: AdvancedEntrySelectProps) => {
  const { t } = useTranslation('receptionsModule');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [allSeenTeams, setAllSeenTeams] = useState<Map<number, { id: number; name: string }>>(new Map());
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const entriesContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(false);

  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const entriesAdapter = useMemo(() => new EntryApiAdapter(httpClient), [httpClient]);
  const clientsAdapter = useMemo(() => new ClientsApiAdapter(httpClient), [httpClient]);
  const productService = useMemo(() => new ProductService(new ProductApiAdapter(httpClient)), [httpClient]);

  const loadClients = useCallback(async () => {
    setIsLoadingClients(true);
    try {
      const response = await clientsAdapter.getClientsPaginated({
        limit: 200,
        page: 1,
      });
      setClients(response.clients);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setIsLoadingClients(false);
    }
  }, [clientsAdapter]);

  // Cargar clientes al montar el componente
  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Cargar entradas cuando cambian los filtros
  useEffect(() => {
    // Resetear y cargar desde el principio cuando cambian los filtros
    setPage(1);
    setEntries([]);
    loadEntries(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedClientId, selectedProductIds]);

  // Cargar opciones de productos para el MultiSelect
  const loadProductsForMultiSelect = useCallback(
    async (p: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      try {
        const result = await productService.getProductsPaginated({
          searchTerm: p.search,
          page: p.page,
          limit: p.limit,
        });
        return {
          options: result.products.map((product) => ({
            value: product.id.toString(),
            label: product.name || `Producto ${product.id}`,
            description: product.code || undefined,
          })),
          hasMore: result.hasMore,
        };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [productService],
  );

  // Cargar entradas
  const loadEntries = useCallback(
    async (reset = false) => {
      if (reset) {
        setPage(1);
        setHasMore(true);
        setIsLoadingEntries(true);
      } else if (isLoadingMoreRef.current || !hasMore || isLoadingEntries) {
        return;
      }

      isLoadingMoreRef.current = true;
      if (!reset) {
        setIsLoadingEntries(true);
      }

      const currentPage = reset ? 1 : page;
      const filters: EntryFilters = {
        page: currentPage,
        per_page: 20,
        status: ['PENDIENTE', 'EN_RECEPCION_PARCIAL'],
        ...(searchTerm.trim() && { search: searchTerm.trim() }),
        ...(selectedClientId && { team_id: parseInt(selectedClientId) }),
        ...(selectedProductIds.length > 0 && { product_ids: selectedProductIds.map(Number) }),
      };

      try {
        const response = await entriesAdapter.getEntries(20, currentPage, filters);

        if (reset) {
          setEntries(response.data);
          setPage(2); // Preparar para la siguiente página
        } else {
          setEntries((prev) => [...prev, ...response.data]);
          setPage((prev) => prev + 1);
        }

        setTotal(response.meta.total);
        setHasMore(response.meta.current_page < response.meta.last_page);
      } catch (error) {
        console.error('Error loading entries:', error);
        setHasMore(false);
      } finally {
        setIsLoadingEntries(false);
        isLoadingMoreRef.current = false;
      }
    },
    [entriesAdapter, searchTerm, selectedClientId, selectedProductIds, page, hasMore, isLoadingEntries],
  );

  // Cargar más entradas (scroll infinito)
  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMoreRef.current || isLoadingEntries) return;
    loadEntries(false);
  }, [hasMore, isLoadingEntries, loadEntries]);

  // IntersectionObserver para scroll infinito
  useEffect(() => {
    if (!sentinelRef.current || !entriesContainerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingMoreRef.current && !isLoadingEntries) {
          loadMore();
        }
      },
      {
        root: entriesContainerRef.current,
        rootMargin: '100px',
        threshold: 0,
      },
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [hasMore, isLoadingEntries, loadMore]);

  // Actualizar la lista persistente de teams cuando se cargan nuevas entradas
  useEffect(() => {
    setAllSeenTeams((prevTeams) => {
      const newTeams = new Map(prevTeams);
      let hasChanges = false;

      entries.forEach((entry) => {
        if (entry.team && entry.team.id && entry.team.name) {
          if (!newTeams.has(entry.team.id)) {
            newTeams.set(entry.team.id, {
              id: entry.team.id,
              name: entry.team.name,
            });
            hasChanges = true;
          }
        }
      });

      return hasChanges ? newTeams : prevTeams;
    });
  }, [entries]);

  // Combinar clientes de teams-child con todos los teams vistos en las entradas
  const allClients = useMemo(() => {
    const clientsMap = new Map<number, { id: number; name: string }>();

    // Agregar clientes de teams-child
    clients.forEach((client) => {
      clientsMap.set(client.id, {
        id: client.id,
        name: client.name,
      });
    });

    // Agregar todos los teams vistos en las entradas (persistente, no solo los actuales)
    allSeenTeams.forEach((team) => {
      if (!clientsMap.has(team.id)) {
        clientsMap.set(team.id, team);
      }
    });

    return Array.from(clientsMap.values());
  }, [clients, allSeenTeams]);

  // Opciones de clientes para el Combobox
  const clientOptions = useMemo(() => {
    const options = [{ label: 'Todos los clientes', value: '' }];
    allClients.forEach((client) => {
      options.push({
        label: client.name,
        value: String(client.id),
      });
    });
    return options;
  }, [allClients]);

  // Formatear fecha
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Obtener color del badge según estado (bg-bg-* + text-* según convención del proyecto)
  const getStatusBadgeColor = (status: EntryStatus) => {
    switch (status) {
      case 'FINALIZADA':
        return 'bg-bg-success text-success';
      case 'EN_RECEPCION':
      case 'EN_RECEPCION_PARCIAL':
        return 'bg-bg-info text-info';
      case 'PENDIENTE':
        return 'bg-bg-warning text-warning';
      case 'CANCELADA':
        return 'bg-bg-error text-error';
      default:
        return 'bg-muted text-foreground';
    }
  };

  // Manejar selección de entrada
  const handleSelectEntry = (entryId: string) => {
    onValueChange(entryId);
  };

  return (
    <div className="space-y-4" data-testid={dataTestId}>
      {/* Filtros - Search and Client Selector */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Buscador */}
        <div className="flex-1 relative">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-complementary-text z-10"
          />
          <Input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('modal.advanced_entry_select.search_placeholder') || placeholder}
            className="pl-10"
            disabled={disabled}
            data-testid="entry-search-input"
          />
        </div>

        {/* Selector de Cliente */}
        <div className="w-full sm:w-56">
          <Combobox
            options={clientOptions}
            value={selectedClientId}
            setValue={setSelectedClientId}
            placeholder={t('modal.advanced_entry_select.client_placeholder') || 'Selector de Cliente'}
            disabled={isLoadingClients || disabled}
            dataTestId="client-selector"
          />
        </div>

        {/* Selector de Producto */}
        <div className="w-full sm:w-56">
          <MultiSelect
            loadOptions={loadProductsForMultiSelect}
            value={selectedProductIds}
            onValueChange={setSelectedProductIds}
            placeholder={t('modal.advanced_entry_select.product_placeholder') || 'Todos los productos'}
            emptyText={t('modal.advanced_entry_select.no_results') || 'Sin resultados'}
            noResultsText={t('modal.advanced_entry_select.no_results') || 'Sin resultados'}
            searchPlaceholder={t('modal.advanced_entry_select.search_products') || 'Buscar productos...'}
            dataTestId="product-selector"
            triggerClassName="h-[38px]"
            pageSize={10}
          />
        </div>
      </div>

      {/* Resultados */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium">{t('modal.advanced_entry_select.results') || 'Resultados'}</h3>
          <span className="text-sm text-complementary-text">
            {total} {t('modal.advanced_entry_select.results_count') || 'resultados'}
          </span>
        </div>

        {/* Listado de Cards */}
        <div ref={entriesContainerRef} className="space-y-3">
          {isLoadingEntries && entries.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-sm text-complementary-text">
              {t('modal.advanced_entry_select.no_results') || 'No se encontraron entradas'}
            </div>
          ) : (
            <>
              {entries.map((entry) => {
                const isSelected = String(entry.id) === value;
                return (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => handleSelectEntry(String(entry.id))}
                    disabled={disabled}
                    className={cn(
                      'w-full text-left p-4 rounded-lg border transition-all',
                      'hover:border-primary hover:shadow-md',
                      disabled && 'opacity-50 cursor-not-allowed',
                      isSelected ? 'border-primary bg-primary/5 shadow-md' : 'border-card-border bg-card-bg',
                    )}
                    data-testid={`entry-card-${entry.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Información principal */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-semibold text-title">{entry.reference}</span>
                          <span
                            className={cn(
                              'px-2 py-0.5 rounded-full text-xs font-medium',
                              getStatusBadgeColor(entry.status as EntryStatus),
                            )}
                          >
                            {ENTRY_STATUS_LABELS[entry.status as EntryStatus] || entry.status}
                          </span>
                        </div>

                        <div className="space-y-1 text-xs text-complementary-text">
                          {entry.team && (
                            <div>
                              <span className="font-medium">Cliente:</span> {entry.team.name}
                            </div>
                          )}
                          {entry.estimated_delivery_at && (
                            <div>
                              <span className="font-medium">Fecha:</span> {formatDate(entry.estimated_delivery_at)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Indicador de selección */}
                      {isSelected && (
                        <div className="flex-shrink-0">
                          <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}

              {/* Sentinel para scroll infinito */}
              {hasMore && <div ref={sentinelRef} className="h-1" />}

              {/* Loading more */}
              {isLoadingEntries && entries.length > 0 && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedEntrySelect;
